/**
 * Delay simulation utilities for mock data
 * Supports: fast, normal, slow, verySlow modes with query param overrides
 */

import { delay } from 'msw'

/**
 * Delay presets in milliseconds
 */
export const DELAY_PRESETS = {
  /** Fast response - 50-150ms */
  fast: { min: 50, max: 150 },
  /** Normal response - 200-500ms */
  normal: { min: 200, max: 500 },
  /** Slow response - 800-1500ms */
  slow: { min: 800, max: 1500 },
  /** Very slow response - 2000-4000ms */
  verySlow: { min: 2000, max: 4000 },
  /** Instant response - no delay */
  instant: { min: 0, max: 0 }
} as const

export type DelayPreset = keyof typeof DELAY_PRESETS

/**
 * Configuration for delay simulation
 */
export interface DelayConfig {
  /** Enable delay simulation */
  enabled: boolean
  /** Default delay preset */
  defaultPreset: DelayPreset
  /** Progressive delay: add extra delay for larger datasets */
  progressiveDelay: boolean
  /** Items per additional delay unit for progressive delays */
  progressiveThreshold: number
  /** Additional delay per threshold (ms) */
  progressiveDelayMs: number
}

const DEFAULT_DELAY_CONFIG: DelayConfig = {
  enabled: true,
  defaultPreset: 'normal',
  progressiveDelay: true,
  progressiveThreshold: 100,
  progressiveDelayMs: 50
}

/**
 * Generate a random delay within a range
 */
function randomDelay(min: number, max: number): number {
  if (min === max) return min
  return Math.floor(Math.random() * (max - min + 1)) + min
}

/**
 * Get delay range for a preset
 */
export function getDelayRange(preset: DelayPreset): { min: number, max: number } {
  return DELAY_PRESETS[preset] || DELAY_PRESETS.normal
}

/**
 * Calculate delay in milliseconds for a preset
 */
export function calculateDelay(preset: DelayPreset): number {
  const range = getDelayRange(preset)
  return randomDelay(range.min, range.max)
}

/**
 * Calculate progressive delay based on dataset size
 */
export function calculateProgressiveDelay(
  itemCount: number,
  config: Partial<Pick<DelayConfig, 'progressiveThreshold' | 'progressiveDelayMs'>> = {}
): number {
  const threshold = config.progressiveThreshold ?? DEFAULT_DELAY_CONFIG.progressiveThreshold
  const delayMs = config.progressiveDelayMs ?? DEFAULT_DELAY_CONFIG.progressiveDelayMs

  if (itemCount <= threshold) return 0

  // Calculate additional delay based on how many thresholds exceeded
  const thresholdMultiplier = Math.floor(itemCount / threshold)
  return Math.min(thresholdMultiplier * delayMs, 500) // Cap at 500ms extra
}

/**
 * Parse delay preset from query parameter
 * Supports: ?_delay=fast or ?_delay=1000 (custom ms)
 */
export function parseDelayParam(searchParams: URLSearchParams): {
  preset?: DelayPreset
  customMs?: number
} {
  const delayParam = searchParams.get('_delay')

  if (!delayParam) return {}

  // Check if it's a preset name
  if (delayParam in DELAY_PRESETS) {
    return { preset: delayParam as DelayPreset }
  }

  // Check if it's a numeric value
  const numericDelay = Number.parseInt(delayParam, 10)
  if (!Number.isNaN(numericDelay) && numericDelay >= 0) {
    return { customMs: numericDelay }
  }

  return {}
}

/**
 * Apply delay simulation
 *
 * @example
 * ```ts
 * // Basic usage with default preset
 * await applyDelay()
 *
 * // With specific preset
 * await applyDelay({ preset: 'slow' })
 *
 * // With progressive delay for large datasets
 * await applyDelay({ preset: 'normal', itemCount: 500 })
 *
 * // With query param override
 * await applyDelay({ searchParams, preset: 'normal' })
 * ```
 */
export async function applyDelay(options: {
  /** URL search params for override */
  searchParams?: URLSearchParams
  /** Delay preset to use */
  preset?: DelayPreset
  /** Custom delay in milliseconds */
  customMs?: number
  /** Item count for progressive delay */
  itemCount?: number
  /** Delay configuration */
  config?: Partial<DelayConfig>
} = {}): Promise<void> {
  const config = { ...DEFAULT_DELAY_CONFIG, ...options.config }

  if (!config.enabled) return

  let delayMs = 0

  // Check for query param override first
  if (options.searchParams) {
    const paramOverride = parseDelayParam(options.searchParams)

    if (paramOverride.customMs !== undefined) {
      delayMs = paramOverride.customMs
    } else if (paramOverride.preset) {
      delayMs = calculateDelay(paramOverride.preset)
    } else {
      // Use provided preset or default
      delayMs = calculateDelay(options.preset ?? config.defaultPreset)
    }
  } else if (options.customMs !== undefined) {
    delayMs = options.customMs
  } else {
    delayMs = calculateDelay(options.preset ?? config.defaultPreset)
  }

  // Add progressive delay if enabled
  if (config.progressiveDelay && options.itemCount) {
    delayMs += calculateProgressiveDelay(options.itemCount, config)
  }

  if (delayMs > 0) {
    await delay(delayMs)
  }
}

/**
 * Create a delay function with preset configuration
 *
 * @example
 * ```ts
 * const fastDelay = createDelayFn('fast')
 * await fastDelay()
 *
 * const customDelay = createDelayFn({ min: 100, max: 200 })
 * await customDelay()
 * ```
 */
export function createDelayFn(
  presetOrRange: DelayPreset | { min: number, max: number }
): () => Promise<void> {
  return async () => {
    let ms: number
    if (typeof presetOrRange === 'string') {
      ms = calculateDelay(presetOrRange)
    } else {
      ms = randomDelay(presetOrRange.min, presetOrRange.max)
    }
    await delay(ms)
  }
}

/**
 * Demo mode aware delay
 * Uses session storage to check for demo mode and custom delay settings
 */
export async function applyDemoDelay(
  defaultPreset: DelayPreset = 'normal',
  searchParams?: URLSearchParams
): Promise<void> {
  // Check for demo mode in client context
  const isDemoMode = typeof globalThis !== 'undefined'
    && typeof globalThis.sessionStorage !== 'undefined'
    && globalThis.sessionStorage.getItem('demo-mode') === 'true'

  // Get demo delay preference
  const demoDelayPreset = isDemoMode
    ? (sessionStorage.getItem('demo-delay') as DelayPreset | null) || 'fast'
    : defaultPreset

  await applyDelay({
    searchParams,
    preset: demoDelayPreset
  })
}
